import React, { useEffect, useRef, useState } from "react";
import { MdOutlineBusiness, MdOutlinePerson, MdOutlineSearch } from "react-icons/md";
import { toast } from "react-toastify";
import * as expenseService from "../../services/expenseService";
import Loader from "../../components/Loader/Loader";
import { labelStyle, inputStyle, cardStyle, badgeStyle } from "./expenseStyles";

const TYPE_LABEL = { INDIVIDUAL: "Individual", COMPANY: "Company" };
const EMPTY_FORM = { type: "INDIVIDUAL", name: "", phone_number: "", email: "", address: "", trn: "" };

const customerMeta = (c) => [c.trn && `TRN ${c.trn}`, c.phone, c.email].filter(Boolean).join(" · ");

function TypeBadge({ type }) {
  return type === "COMPANY" ? (
    <span style={badgeStyle("#E0E7FF", "#3730A3")}>{TYPE_LABEL.COMPANY}</span>
  ) : (
    <span style={badgeStyle("#F3F4F6", "#374151")}>{TYPE_LABEL.INDIVIDUAL}</span>
  );
}

// The Bill To of an invoice: search existing customers (individuals from the
// shared Customers list and companies) or create a new one. Controlled:
// `value` is the selected customer ({ type, id, name, ... }) or null and
// `onChange` receives the new selection.
function CustomerPicker({ value, onChange }) {
  const [mode, setMode] = useState("search"); // search | create
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const latestSearch = useRef(0);

  // Debounced search; an empty query lists the most recent customers. Only the
  // newest request may update the list, so a slow early response can't clobber
  // a later one.
  useEffect(() => {
    if (mode !== "search" || value) return undefined;
    const ticket = ++latestSearch.current;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await expenseService.searchCustomers(query.trim());
        if (ticket === latestSearch.current) setResults(res?.success && Array.isArray(res.data) ? res.data : []);
      } catch {
        if (ticket === latestSearch.current) setResults([]);
      } finally {
        if (ticket === latestSearch.current) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, mode, value]);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const startCreate = () => {
    // Carry what was typed into the search box over to the new customer.
    const typed = query.trim();
    const looksLikePhone = /^[+\d][\d\s-]{5,}$/.test(typed);
    setForm({ ...EMPTY_FORM, name: looksLikePhone ? "" : typed, phone_number: looksLikePhone ? typed : "" });
    setError("");
    setMode("create");
  };

  const handleCreate = async () => {
    const isCompany = form.type === "COMPANY";
    if (!form.name.trim()) return setError(isCompany ? "Company name is required" : "Customer name is required");
    if (!isCompany && !form.phone_number.trim()) return setError("Phone number is required for an individual customer");
    setError("");
    setSaving(true);
    try {
      const res = await expenseService.createCustomer({
        type: form.type,
        name: form.name.trim(),
        phone_number: form.phone_number.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        ...(isCompany ? { trn: form.trn.trim() } : {}),
      });
      if (!res?.success) return setError(expenseService.responseMessage(res, "Could not create the customer"));
      if (res.data?.existing) toast.info("This customer already exists - selected the existing record");
      onChange(res.data);
      setMode("search");
    } catch (err) {
      setError(err.message || "Could not create the customer");
    } finally {
      setSaving(false);
    }
  };

  if (value) {
    return (
      <div className="d-flex justify-content-between align-items-start gap-3" style={{ ...cardStyle, border: "1px solid #F5C6D3", background: "#FDF2F5", padding: "12px 16px" }}>
        <div style={{ minWidth: 0 }}>
          <div className="d-flex align-items-center gap-2 mb-1">
            <strong style={{ fontSize: "14px", color: "#111827" }}>{value.name}</strong>
            <TypeBadge type={value.type} />
          </div>
          <div style={{ fontSize: "12px", color: "#6B7280" }}>{customerMeta(value) || "No contact details"}</div>
          {value.address && <div style={{ fontSize: "12px", color: "#6B7280" }}>{value.address}</div>}
        </div>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => onChange(null)}>
          Change
        </button>
      </div>
    );
  }

  if (mode === "create") {
    const isCompany = form.type === "COMPANY";
    return (
      <div style={{ ...cardStyle, padding: "16px" }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <strong style={{ fontSize: "14px", color: "#111827" }}>New customer</strong>
          <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setMode("search")} disabled={saving}>
            Back to search
          </button>
        </div>

        <div className="btn-group mb-3" role="group" aria-label="Customer type">
          {Object.keys(TYPE_LABEL).map((type) => (
            <button
              key={type}
              type="button"
              className={`btn btn-sm ${form.type === type ? "btn-danger" : "btn-outline-secondary"}`}
              onClick={() => setForm((f) => ({ ...f, type }))}
              disabled={saving}
            >
              {type === "COMPANY" ? <MdOutlineBusiness size={15} /> : <MdOutlinePerson size={15} />} {TYPE_LABEL[type]}
            </button>
          ))}
        </div>

        <div className="row g-3">
          <div className="col-12">
            <label htmlFor="cust-name" style={labelStyle}>{isCompany ? "Company name *" : "Full name *"}</label>
            <input id="cust-name" className="form-control" style={inputStyle} value={form.name} onChange={setField("name")} maxLength={200} />
          </div>
          <div className="col-md-6">
            <label htmlFor="cust-phone" style={labelStyle}>{isCompany ? "Phone" : "Phone *"}</label>
            <input id="cust-phone" className="form-control" style={inputStyle} value={form.phone_number} onChange={setField("phone_number")} maxLength={30} />
          </div>
          <div className="col-md-6">
            <label htmlFor="cust-email" style={labelStyle}>Email</label>
            <input id="cust-email" type="email" className="form-control" style={inputStyle} value={form.email} onChange={setField("email")} maxLength={150} />
          </div>
          {isCompany && (
            <div className="col-md-6">
              <label htmlFor="cust-trn" style={labelStyle}>TRN</label>
              <input id="cust-trn" className="form-control" style={inputStyle} value={form.trn} onChange={setField("trn")} maxLength={50} />
            </div>
          )}
          <div className="col-12">
            <label htmlFor="cust-address" style={labelStyle}>Address</label>
            <textarea id="cust-address" className="form-control" rows={2} style={{ fontSize: "14px", borderRadius: "6px" }} value={form.address} onChange={setField("address")} maxLength={500} />
          </div>
        </div>

        {error && (
          <div className="alert alert-danger py-2 mt-3 mb-0" style={{ fontSize: "13px" }}>
            {error}
          </div>
        )}

        <div className="d-flex justify-content-end mt-3">
          <button type="button" className="btn Button primaryBackground text-white px-4" onClick={handleCreate} disabled={saving}>
            {saving ? <Loader style={{ width: 40, height: 20 }} /> : "Create & Select"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <label htmlFor="cust-search" style={labelStyle}>Customer *</label>
      <div className="position-relative mb-2">
        <MdOutlineSearch size={18} style={{ position: "absolute", left: 10, top: 11, color: "#9CA3AF" }} />
        <input
          id="cust-search"
          className="form-control"
          style={{ ...inputStyle, paddingLeft: 34 }}
          placeholder="Search by name, phone, email or TRN"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
        />
      </div>

      <div style={{ ...cardStyle, border: "1px solid #E5E7EB", maxHeight: 240, overflowY: "auto" }}>
        {searching && results.length === 0 ? (
          <div style={{ padding: "14px 16px", fontSize: "13px", color: "#6B7280" }}>Searching...</div>
        ) : results.length === 0 ? (
          <div style={{ padding: "14px 16px", fontSize: "13px", color: "#6B7280" }}>
            {query.trim() ? "No customer found." : "No customers yet."}
          </div>
        ) : (
          results.map((c) => (
            <button
              key={`${c.type}-${c.id}`}
              type="button"
              onClick={() => onChange(c)}
              className="d-flex justify-content-between align-items-center gap-2 w-100 text-start"
              style={{ background: "none", border: "none", borderBottom: "1px solid #F3F4F6", padding: "10px 16px" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FAFB")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
            >
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontSize: "14px", fontWeight: 600, color: "#111827" }}>{c.name}</span>
                <span style={{ display: "block", fontSize: "12px", color: "#6B7280" }}>{customerMeta(c) || "No contact details"}</span>
              </span>
              <TypeBadge type={c.type} />
            </button>
          ))
        )}
      </div>

      <button type="button" className="btn btn-outline-secondary btn-sm mt-2" onClick={startCreate}>
        + {query.trim() ? `Create new customer "${query.trim()}"` : "Create new customer"}
      </button>
    </div>
  );
}

export default CustomerPicker;
